import { Percent } from "lucide-react";
import { useRef, useState } from "react";

import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { sanitizeCurrencyInput } from "../../../lib/utils";
import { brl } from "../lib/formato";

// R$ estilo caixa registradora tratado pela tecla (30/09/2026): o lugar do
// cursor não importa — clicar no meio de "0,00" e digitar 700 dava R$ 70,00
// (ou o teto). Ao entrar no campo, o 1º dígito começa um valor novo.
function useTeclasReais({ subtotal, valorDesc, setValorDesc, setPercent, setDigitadoEm }) {
  const novoRS = useRef(false);
  const gravarRS = (cents) => {
    setDigitadoEm("fixed");
    const desc = Math.max(0, Math.min(subtotal, cents / 100));
    setValorDesc(desc.toFixed(2));
    setPercent(subtotal > 0 ? Number(Math.max(0, Math.min(100, (desc / subtotal) * 100)).toFixed(2)) : 0);
  };
  const teclaRS = (e) => {
    const cents = Math.round((Number(valorDesc) || 0) * 100);
    if (/^\d$/.test(e.key) && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault();
      const base = novoRS.current ? 0 : cents;
      novoRS.current = false;
      if (base < 1e8) gravarRS(base * 10 + Number(e.key));
    } else if (e.key === "Backspace") {
      e.preventDefault();
      novoRS.current = false;
      gravarRS(Math.floor(cents / 10));
    }
  };
  const focoRS = (e) => { novoRS.current = true; e.target.select(); };
  return { teclaRS, focoRS };
}

// % e R$ sincronizados: mexer num recalcula o outro sobre o valor da rodada.
// O tipo gravado é o do campo que a pessoa digitou (30/09/2026): digitou R$,
// fica "fixed" — antes o R$ virava % quando o percentual de 2 casas batia com
// o valor, e no Balcão o desconto crescia junto com o carrinho.
export default function DescontoManual({ subtotal, onAplicar, onVoltar }) {
  const [percent, setPercent] = useState(0);
  const [valorDesc, setValorDesc] = useState("");
  const [digitadoEm, setDigitadoEm] = useState(null); // "percent" | "fixed"
  const desconto = Math.max(0, Math.min(subtotal, Number(valorDesc) || 0));
  const { teclaRS, focoRS } = useTeclasReais({ subtotal, valorDesc, setValorDesc, setPercent, setDigitadoEm });

  const aplicar = () => onAplicar(digitadoEm === "percent" && Number(percent) > 0 ? { tipo: "percent", valor: Number(percent) } : { tipo: "fixed", valor: desconto });

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-1.5 text-muted-foreground">
        <Percent className="w-3 h-3" />
        <span className="text-[10px] font-semibold uppercase tracking-wider">Ajuste sincronizado</span>
      </div>
      <div className="grid grid-cols-2 gap-2.5">
        <div>
          <label className="text-[10px] font-semibold text-muted-foreground block mb-1">Percentual</label>
          <div className={`relative rounded-lg border-2 transition-all ${Number(percent) > 0 ? "border-orange-500/60 bg-orange-500/5" : "border-border bg-background"} focus-within:border-orange-500`}>
            <Input
              type="text"
              inputMode="decimal"
              value={percent === 0 ? "" : String(percent).replace(".", ",")}
              onFocus={(e) => e.target.select()}
              onChange={(e) => {
                const pct = Math.max(0, Math.min(100, Number(sanitizeCurrencyInput(e.target.value)) || 0));
                setDigitadoEm("percent"); setPercent(pct);
                setValorDesc((subtotal * (pct / 100)).toFixed(2));
              }}
              placeholder="0"
              className="h-10 text-base font-bold text-center border-0 shadow-none focus-visible:ring-0 bg-transparent pr-8"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-bold text-orange-500 pointer-events-none">%</span>
          </div>
        </div>
        <div>
          <label className="text-[10px] font-semibold text-muted-foreground block mb-1">Desconto em R$</label>
          <div className={`relative rounded-lg border-2 transition-all ${desconto > 0 ? "border-orange-500/60 bg-orange-500/5" : "border-border bg-background"} focus-within:border-orange-500`}>
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold text-muted-foreground pointer-events-none">R$</span>
            <Input
              type="text"
              inputMode="numeric"
              value={(Number(valorDesc) || 0).toFixed(2).replace(".", ",")}
              onKeyDown={teclaRS}
              onFocus={focoRS}
              onChange={(e) => {
                // Máscara de caixa registradora (v1.5.527): cada dígito entra pelo centavo
                const digits = String(e.target.value).replace(/\D/g, "").slice(0, 9);
                const desc = Math.max(0, Math.min(subtotal, parseInt(digits || "0", 10) / 100));
                setDigitadoEm("fixed"); setValorDesc(desc.toFixed(2));
                setPercent(subtotal > 0 ? Number(Math.max(0, Math.min(100, (desc / subtotal) * 100)).toFixed(2)) : 0);
              }}
              placeholder="0,00"
              className="h-10 text-base font-bold text-center border-0 shadow-none focus-visible:ring-0 bg-transparent pl-10"
            />
          </div>
        </div>
      </div>
      {desconto <= 0 ? (
        <p className="text-[11px] text-center text-muted-foreground italic">Informe o desconto em % ou R$</p>
      ) : (
        <div className="p-2.5 bg-green-500/10 border border-green-500/30 rounded-lg space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-xs text-green-700 dark:text-green-400 font-semibold">Desconto total</span>
            <span className="text-base font-black text-green-600 dark:text-green-500">− {brl(desconto)}</span>
          </div>
          <div className="flex items-center justify-between text-[11px] text-muted-foreground">
            <span>Valor final</span>
            <span className="font-mono">{brl(Math.max(0, subtotal - desconto))}</span>
          </div>
        </div>
      )}
      <div className="flex gap-2 pt-0.5">
        <Button variant="outline" className="flex-1 h-9" onClick={onVoltar}>Voltar</Button>
        <Button className="flex-[1.5] h-9 bg-orange-500 hover:bg-orange-600 font-semibold" disabled={!(desconto > 0)} onClick={aplicar}>
          Aplicar desconto
        </Button>
      </div>
    </div>
  );
}
